/* eslint-disable react/require-default-props */
/**
 * LaundryBundleMiniContent component renders the UI for building a matching laundry set bundle.
 * It displays a title, a link to shop all laundry sets, containers for washer/dryer products,
 * accessories, and the bundle price. It also handles analytics event triggering when viewing set details.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array<Object>} props.products - Array of product objects for washer and dryer.
 * @param {Object|null} props.accessories - Accessories object or null if not present.
 * @param {Function} props.onSwapProduct - Callback to handle swapping washer/dryer products.
 * @param {Function} props.onSwapAccessory - Callback to handle swapping accessories.
 * @param {Object} props.bundle - Bundle object containing pricing and identifiers.
 * @param {Array<Object>} props.selectedProducts - Array of currently selected products.
 * @returns {JSX.Element} The rendered LaundryBundleMiniContent component.
 */
/* eslint-disable @harmony/stencil/no-tailwind-disable */
/* eslint-disable max-len */
/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useRef } from 'react';
import proptypes from 'prop-types';
const {
  shape, func, arrayOf, bool
} = proptypes;
import { extend } from '@thd-nucleus/data-sources';
import { Typography, Link } from '@one-thd/sui-atomic-components';
import WasherDryerContainer from './WasherDryerContainer';
import AccessoriesContainer from './AccessoriesContainer';
import { LAUNDRY_VIEW_BUNDLE } from '../constants/index';
import LaundryProductsLayout from './LaundryProductsLayout';
import { LaundryPrice } from './LaundryPrice';
import { Symbol } from './laundry/Symbol';
import { AlignedContent } from './laundry/AlignedContent';
import { tiggerAnalyticsCustomEvent } from '../utils/product-utils';
import { ImpressionRunner } from './ImpressionRunner';

/**
* LaundryBundleMiniContent component renders a section for building a matching laundry set.
* It displays a title, a link to shop all laundry sets, container for washer and dryer products,
* container for accessories and bundle price.
*
* @param {Object} props - The props object.
* @param {Object} props.products - An object containing the washer and dryer products.
* @param {Object} props.products.WASHER - The washer product details.
* @param {Object} props.products.DRYER - The dryer product details.
* @param {Object} props.products.ACCESSORIES - The accessory product details.
* @param {Function} props.onSwapVariant - Callback function to handle swapping product variants.
* @param {Object} props.bundle - The bundle object containing pricing details.
* @param {Array} props.selectedProducts - An array of selected products.
*
* @returns {JSX.Element} The rendered LaundryBundleMiniContent component.
*/
export const LaundryBundleMiniContent = ({
  products,
  accessories = null,
  onSwapProduct,
  onSwapAccessory,
  bundle,
  isBundleModified = false
}) => {
  const anchorRef = useRef(null);
  const triggerAnalytics = () => {
    const output = {
      itemId: bundle?.itemId,
      product: bundle
    };
    tiggerAnalyticsCustomEvent(LAUNDRY_VIEW_BUNDLE, output);
  };
  return (
    <>
      <ImpressionRunner targetRef={anchorRef} bundleId={bundle?.itemId} />
      <div ref={anchorRef} className="sui-bg-primary sui-p-3 sui-mx-0 sui-my-4 md:sui-mx-0 md:sui-my-6 lg:sui-mx-0 lg:sui-my-6 sui-py-6" data-testid="laundry-entry-point">
        <div className="sui-flex sui-flex-col md:sui-flex-row md:sui-items-center sui-gap-1
      md:sui-gap-0 sui-pt-4 md:sui-pt-6 lg:sui-pt-0 sui-pb-4 sui-justify-between"
        >
          <Typography variant="h2">Build A Matching Set</Typography>
          <Link
            href="https://www.homedepot.com/b/Appliances-Washers-Dryers-Washer-Dryer-Sets/N-5yc1vZ2fkpgkd"
            target="_blank"
            variant="body-sm"
          >Shop All Laundry Sets
          </Link>
        </div>
        <LaundryProductsLayout
          grid={{
            md: 12
          }}
          element="section"
        >
          <div className="md:sui-col-span-7 lg:sui-col-span-5">
            <WasherDryerContainer
              products={products}
              onSwap={onSwapProduct}
            />
          </div>
          {
            accessories?.products?.length > 0 && (
              <>
                <AlignedContent
                  wrapperClassName="sui-my-6 md:sui-mb-0 md:sui-mt-10"
                >
                  <Symbol symbol="+" />
                </AlignedContent>
                <div className="md:sui-col-span-4 md:sui-col-start-9 lg:sui-col-span-2 lg:sui-col-start-7">
                  <AccessoriesContainer accessories={accessories} onSwap={onSwapAccessory} />
                </div>
              </>
            )
          }
          <AlignedContent
            wrapperClassName="sui-hidden md:sui-block md:sui-mt-10 md:sui-col-start-1 lg:sui-col-start-auto"
            contentClassName="md:sui-items-start lg:sui-items-center md:sui-justify-start lg:sui-justify-center"
          >
            <Symbol symbol="=" />
          </AlignedContent>
          {
            bundle && products && (
              <div className="sui-w-full sui-mt-4 md:sui-mt-6 md:sui-col-span-5 lg:sui-col-span-3 sui-flex sui-flex-col sui-gap-3 lg:sui-mt-[calc(40px_+_18%)]">
                <LaundryPrice bundle={bundle} products={products} accessories={accessories} isBundleModified={isBundleModified} />
                <span className="sui-self-center">
                  <Link
                    href={bundle?.identifiers?.canonicalUrl}
                    target="_blank"
                    color="subtle"
                    underline="always"
                    variant="body-sm"
                    height="tight"
                    onClick={triggerAnalytics}
                  >
                    View Set Details
                  </Link>
                </span>
              </div>
            )
          }
        </LaundryProductsLayout>
      </div>
    </>

  );
};
LaundryBundleMiniContent.propTypes = {
  products: arrayOf(shape({})).isRequired,
  accessories: shape({}),
  onSwapProduct: func.isRequired,
  onSwapAccessory: func.isRequired,
  bundle: shape({}).isRequired,
  isBundleModified: bool
};

LaundryBundleMiniContent.dataModel = extend(
  WasherDryerContainer,
  AccessoriesContainer,
  LaundryPrice
);